import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { toast } from 'sonner';
import { 
  Upload, 
  Download, 
  FileSpreadsheet, 
  Package, 
  AlertCircle, 
  CheckCircle2,
  Loader2 
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const ImportTab = ({ stats, texts, onRefresh }) => {
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [replaceAll, setReplaceAll] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setImportResult(null);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      toast.error('Por favor selecciona un archivo');
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const token = localStorage.getItem('auth_token');
      const response = await axios.post(`${API}/admin/products/import?replace_all=${replaceAll}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });

      setImportResult(response.data);
      toast.success(`Importación completada: ${response.data.imported} productos`);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.detail || 'Error al importar productos');
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API}/admin/products/template`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'plantilla_productos.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Template download error:', error);
      toast.error('Error al descargar la plantilla');
    }
  };

  const handleExport = async (exportAll = true) => {
    try {
      const token = localStorage.getItem('auth_token');
      const params = exportAll ? { export_all: 'true' } : {};

      const response = await axios.get(`${API}/admin/products/export`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
        responseType: 'blob',
        timeout: 120000,
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `productos_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Exportación completada');
    } catch (error) {
      console.error('Export error:', error);
      if (error.code === 'ECONNABORTED') {
        toast.error('La exportación tardó demasiado. Intenta con menos filtros.');
      } else {
        toast.error('Error al exportar productos');
      }
    }
  };

  return (
    <div data-testid="import-tab">
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Import Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-primary" />
              {texts.importTitle}
            </CardTitle>
            <CardDescription>{texts.importDescription}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
              />
              <FileSpreadsheet className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              {selectedFile ? (
                <div>
                  <p className="font-medium text-foreground">{selectedFile.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              ) : (
                <p className="text-muted-foreground">
                  Arrastra un archivo o haz clic para seleccionar
                </p>
              )}
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => fileInputRef.current?.click()}
              >
                {texts.selectFile}
              </Button>
            </div>

            <div className="flex items-start space-x-2 p-3 bg-warning/10 rounded-lg">
              <Checkbox
                id="replaceAll"
                checked={replaceAll}
                onCheckedChange={setReplaceAll}
              />
              <div className="space-y-1">
                <Label htmlFor="replaceAll" className="cursor-pointer font-medium">
                  {texts.replaceAll}
                </Label>
                <p className="text-sm text-muted-foreground">
                  {texts.replaceAllWarning}
                </p>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                className="flex-1"
                onClick={handleImport}
                disabled={!selectedFile || importing}
              >
                {importing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {texts.importing}
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    {texts.importButton}
                  </>
                )}
              </Button>
              <Button variant="outline" onClick={handleDownloadTemplate}>
                <Download className="h-4 w-4 mr-2" />
                {texts.downloadTemplate}
              </Button>
            </div>

            {/* Import Result */}
            {importResult && (
              <Alert variant={importResult.errors > 0 ? 'destructive' : 'default'}>
                {importResult.errors > 0 ? (
                  <AlertCircle className="h-4 w-4" />
                ) : (
                  <CheckCircle2 className="h-4 w-4" />
                )}
                <AlertTitle>{texts.importSuccess}</AlertTitle>
                <AlertDescription>
                  <p>
                    {importResult.imported} {texts.importedRows}, {importResult.errors}{' '}
                    {texts.errorRows}
                  </p>
                  {importResult.error_details?.length > 0 && (
                    <ul className="mt-2 text-sm list-disc list-inside">
                      {importResult.error_details.slice(0, 5).map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                      {importResult.error_details.length > 5 && (
                        <li>...y {importResult.error_details.length - 5} mas</li>
                      )}
                    </ul>
                  )}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Export Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="h-5 w-5 text-primary" />
              {texts.export}
            </CardTitle>
            <CardDescription>
              Descarga el catalogo de productos en formato Excel
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-6 bg-muted/50 rounded-lg text-center">
              <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-2xl font-bold">{stats?.total_products?.toLocaleString() || 0}</p>
              <p className="text-muted-foreground">productos en el catalogo</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Button variant="outline" className="h-20 flex-col" onClick={() => handleExport(true)}>
                <Download className="h-6 w-6 mb-2" />
                {texts.exportAll}
              </Button>
              <Button variant="outline" className="h-20 flex-col" onClick={() => handleExport(false)}>
                <FileSpreadsheet className="h-6 w-6 mb-2" />
                {texts.exportPage}
              </Button>
            </div>

            <p className="text-sm text-muted-foreground text-center">
              El archivo se descargara en formato Excel (.xlsx)
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Column Reference */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="text-lg">Columnas del Excel</CardTitle>
          <CardDescription>
            El archivo Excel debe contener las siguientes columnas (el orden no importa):
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 text-sm">
            {[
              'Website',
              'Pais',
              'Categoria',
              'Casino',
              'Cbd',
              'Gambling',
              'Etiqueta',
              'Precio Venta',
              'Precio material sensible',
              'Mas informacion',
              'Available',
              'DR',
              'Trafico organico ahrefs',
              'Idioma',
              'Periodico, web o blog',
              'Tipo de enlace',
              'Duracion de enlace',
            ].map((col) => (
              <div key={col} className="flex items-center gap-2 p-2 bg-muted/50 rounded">
                <div className="w-2 h-2 rounded-full bg-primary" />
                <span>{col}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ImportTab;
